import { $nodeSchema, $remark } from '@milkdown/utils'
import type { MarkdownNode } from '@milkdown/transformer'
import type { Options as RemarkStringifyOptions } from 'remark-stringify'

import {
  MDAST_SNIPPET_BLOCK,
  snippetIdFromLine,
  snippetReferenceMarkdown,
} from './snippetToken'

function paragraphLines(children: MarkdownNode[]): MarkdownNode[][] {
  const lines: MarkdownNode[][] = [[]]

  for (const child of children) {
    if (child.type === 'break') {
      lines.push([])
      continue
    }
    if (child.type !== 'text' || typeof child.value !== 'string') {
      lines.at(-1)?.push(child)
      continue
    }

    const parts = child.value.split('\n')
    parts.forEach((part, index) => {
      if (part) lines.at(-1)?.push({ type: 'text', value: part })
      if (index < parts.length - 1) lines.push([])
    })
  }

  return lines
}

function splitSnippetParagraph(node: MarkdownNode): MarkdownNode[] {
  if (node.type !== 'paragraph' || !node.children) return [node]

  const lines = paragraphLines(node.children)
  if (
    !lines.some(
      (line) =>
        line.length === 1 &&
        line[0]?.type === 'text' &&
        typeof line[0].value === 'string' &&
        snippetIdFromLine(line[0].value)
    )
  ) {
    return [node]
  }

  const result: MarkdownNode[] = []
  let paragraphChildren: MarkdownNode[] = []
  const flushParagraph = () => {
    if (!paragraphChildren.length) return
    result.push({ type: 'paragraph', children: paragraphChildren })
    paragraphChildren = []
  }

  lines.forEach((line, index) => {
    const id =
      line.length === 1 &&
      line[0]?.type === 'text' &&
      typeof line[0].value === 'string'
        ? snippetIdFromLine(line[0].value)
        : null

    if (id) {
      flushParagraph()
      result.push({
        type: MDAST_SNIPPET_BLOCK,
        value: `{snippet:${id}}`,
      })
      return
    }

    if (paragraphChildren.length && index > 0) {
      paragraphChildren.push({ type: 'break', data: { isInline: true } })
    }
    paragraphChildren.push(...line)
  })
  flushParagraph()
  return result
}

/** Promote root-level reference lines before the inline Jinja transformer runs. */
export const remarkSnippetBlock = $remark('remarkSnippetBlock', () => () => {
  return (tree) => {
    const root = tree as MarkdownNode
    if (!root.children) return
    root.children = root.children.flatMap(splitSnippetParagraph)
  }
})

export const snippetBlockSchema = $nodeSchema(MDAST_SNIPPET_BLOCK, () => ({
  group: 'block',
  atom: true,
  // Not selectable: a NodeSelection paints the whole preview as one highlight
  // on a plain click. Read-only body text should select like any other text.
  selectable: false,
  attrs: { id: { default: '', validate: 'string' } },
  // Copying a selection across this atom would otherwise yield no text for it.
  leafText: (node) => snippetReferenceMarkdown(String(node.attrs.id)) ?? '',
  parseDOM: [
    {
      tag: 'div[data-snippet-id]',
      getAttrs: (dom) => ({
        id: dom instanceof HTMLElement ? (dom.dataset.snippetId ?? '') : '',
      }),
    },
  ],
  // The nodeView owns the editor's rendering; this is what static renders
  // (a reference nested inside a snippet preview) and the clipboard see.
  toDOM: (node) => [
    'div',
    { 'data-snippet-id': String(node.attrs.id), 'data-snippet-block': '' },
    snippetReferenceMarkdown(String(node.attrs.id)) ?? '',
  ],
  parseMarkdown: {
    match: (node) => node.type === MDAST_SNIPPET_BLOCK,
    runner: (state, node, type) => {
      const value = typeof node.value === 'string' ? node.value : ''
      state.addNode(type, { id: snippetIdFromLine(value) ?? '' })
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === MDAST_SNIPPET_BLOCK,
    runner: (state, node) => {
      const value = snippetReferenceMarkdown(String(node.attrs.id))
      if (value) state.addNode(MDAST_SNIPPET_BLOCK, undefined, value)
    },
  },
}))

type StringifyHandle = NonNullable<
  NonNullable<RemarkStringifyOptions['handlers']>[keyof NonNullable<
    RemarkStringifyOptions['handlers']
  >]
>

const stringifySnippetBlock: StringifyHandle = (node) => {
  const value = (node as { value?: unknown }).value
  return typeof value === 'string' ? value : ''
}

export const snippetBlockStringifyHandlers: Record<string, StringifyHandle> = {
  [MDAST_SNIPPET_BLOCK]: stringifySnippetBlock,
}
